import { useEffect, useState } from "react";
import {
  Assessment,
  AssessmentEvent,
  SubmitAssessmentRequestInput,
  UpdateAssessmentInput,
  User
} from "../../API";
import { QueryStatus } from "../../app/types";
import {
  AssessmentRoleResponse,
  fetchAllAssessmentEvents,
  fetchAllAssessments,
  fetchAssessment,
  fetchAssessmentsByInstrumentId,
  getAssessmentRole,
  submitAssessmentRequest as submitAssessmentRequestAPI,
  updateAssessment as updateAssessmentAPI
} from "./assessmentsAPI";

export const useAllAssessments = () => {
  const [assessments, setAssessments] = useState<Assessment[] | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getAllAssessments = async () => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchAllAssessments();
      setAssessments(results);
      setStatus("succeeded");
      return results;
    } catch (error) {
      const errorString = `${error}`;
      console.error("error fetching all assessments:", error);
      setStatus("failed");
      setError(errorString);
      return null;
    }
  };

  useEffect(() => {
    getAllAssessments();
  }, []);

  return {
    assessments,
    status,
    error,
    getAllAssessments
  };
};

export const useAllAssessmentEvents = () => {
  const [assessmentEvents, setAssessmentEvents] = useState<
    AssessmentEvent[] | null
  >(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getAllAssessmentEvents = async () => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchAllAssessmentEvents();
      setAssessmentEvents(results);
      setStatus("succeeded");
      return results;
    } catch (error) {
      const errorString = `${error}`;
      console.error("error fetching all assessment events:", error);
      setStatus("failed");
      setError(errorString);
      return null;
    }
  };

  useEffect(() => {
    getAllAssessmentEvents();
  }, []);

  return {
    assessmentEvents,
    status,
    error,
    getAllAssessmentEvents
  };
};

export const useAssessment = (id?: string) => {
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getAssessment = async (id: string) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await fetchAssessment(id);
      setAssessment(result);
      setStatus("succeeded");
      return result;
    } catch (error) {
      console.error("error fetching assessment:", error);
      const errorString = `${error}`;
      setStatus("failed");
      setError(errorString);
      return null;
    }
  };

  useEffect(() => {
    if (id) {
      getAssessment(id);
    }
  }, [id]);

  return {
    assessment,
    status,
    error,
    getAssessment
  };
};

export const useAssessmentsByInstrumentId = (id?: string) => {
  const [assessments, setAssessments] = useState<Assessment[] | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getAssessmentsByInstrumentId = async (id: string) => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchAssessmentsByInstrumentId(id);
      setAssessments(results);
      setStatus("succeeded");
      return results;
    } catch (error) {
      console.error("error fetching assessment by instrument id :", error);
      const errorString = JSON.stringify(error);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  useEffect(() => {
    if (id) {
      getAssessmentsByInstrumentId(id);
    }
  }, [id]);

  return {
    assessments,
    status,
    error,
    getAssessmentsByInstrumentId
  };
};

export const useUpdateAssessment = () => {
  const [updatedAssessment, setUpdatedAssessment] = useState<Assessment | null>(
    null
  );
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const updateAssessment = async (input: UpdateAssessmentInput) => {
    setStatus("loading");
    setError(null);

    try {
      const updateResult = await updateAssessmentAPI(input);
      setUpdatedAssessment(updateResult);
      setStatus("succeeded");
      return updateResult;
    } catch (error) {
      console.error("error in updateAssessment:", error);
      const errorString = JSON.stringify(error);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };
  return {
    updatedAssessment,
    status,
    error,
    updateAssessment
  };
};

export const useSubmitAssessmentRequest = () => {
  const [assessmentEventToStart, setAssessmentEventToStart] =
    useState<AssessmentEvent | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const submitAssessmentRequest = async (
    input: SubmitAssessmentRequestInput
  ) => {
    setStatus("loading");
    setError(null);

    try {
      const newAssessmentEventToStart = await submitAssessmentRequestAPI(input);
      setAssessmentEventToStart(newAssessmentEventToStart);
      setStatus("succeeded");
    } catch (error) {
      console.error("error submitting assessment request:", error);
      const anyError = error as any;
      const errorMessage = anyError?.errors?.[0]?.message;
      const fullErrorMessage = `Error submitting assessment request: ${
        errorMessage ?? "Unknown Error"
      }`;
      setStatus("failed");
      setError(fullErrorMessage);
    }
  };

  return {
    assessmentEventToStart,
    status,
    error,
    submitAssessmentRequest
  };
};

export const AssessmentNetworkErrors = {
  FetchAllAssessmentsError:
    "Error fetching assessments. Please try again or contact your administrator.",
  FetchAssessmentError:
    "Error fetching assessment. Please check the assessment id and try again or contact your administrator.",
  AssessmentNotFound: "Assessment not found!"
};

export const AssessmentErrors = {
  NoActivities: "No Activities",
  NoInstrument: "No Instrument"
};

export const useAssessmentRoleRequest = (userId?: string) => {
  const [assessmentRole, setAssessmentRole] =
    useState<AssessmentRoleResponse | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getAssessmentRoleAPI = async () => {
    setStatus("loading");
    setError(null);

    try {
      const assessmentRoleResponse = await getAssessmentRole(userId);

      setAssessmentRole(assessmentRoleResponse);
      setStatus("succeeded");
    } catch (error) {
      console.error("error fetching assessment role api:", error);
      const anyError = error as any;
      const errorMessage = anyError?.errors?.[0]?.message;
      const fullErrorMessage = `Error fetching assessment role: ${
        errorMessage ?? "Unknown Error"
      }`;
      setStatus("failed");
      setError(fullErrorMessage);
    }
  };

  useEffect(() => {
    if (userId) {
      getAssessmentRoleAPI();
    }
  }, [userId]);

  return {
    assessmentRole,
    status,
    error,
    getAssessmentRoleAPI
  };
};
